import React from 'react';
import PropTypes from 'prop-types';
import { Button, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// HOOKS
import useDialog from '@/hooks/useDialog';

// COMPONENTS
import FundsDialog from '@/components/StreamingRewards/Funds/FundsDialog';
import Loader from '@/components/Loader';

// STYLES
import { SRFundsPaymentFormStyled } from './styles';
import { formatBalance } from '@/utility/currency';
import { PersonalFundsType } from '@/constants/streamingRewards';
import PartialPayment from '@/containers/Subscription/help/SubscriptionForm/help/PaymentForm/help/PartialPayment';
import { getDefaultUserCard } from '@/utility/user';

function SRFundsPaymentForm(props) {
  const {
    virtualCard,
    loading,
    onSubmit,
    cardReqParams,
    totalPrice,
    disabled,
    price,
    trialDays,
    isSkipCheckoutFlow,
    stripeInitValues = {},
  } = props;
  const [t] = useTranslation();
  const dialog = useDialog();

  const { user, cards, loadingUpdateSettings, fundsPaymentLoading } = useSelector(
    ({ auth, user: userStore, streamingRewardsFunds }) => ({
      user: auth.user || {},
      cards: userStore.cards,
      loadingUpdateSettings: userStore.loadings.updateSettings,
      fundsPaymentLoading: streamingRewardsFunds.loadings.payment,
    }),
  );

  const topUpAllowed = virtualCard?.personal_funds_type === PersonalFundsType.TOP_UP;
  const PartialPaymentAllowed =
    virtualCard?.personal_funds_type === PersonalFundsType.PARTIAL_PAYMENT;
  const defaultCard = getDefaultUserCard(cards, true);
  const hasPartialPaymentOption =
    PartialPaymentAllowed && user.card_payments_allowed && !!defaultCard;
  const disabledSubmitBtn =
    (totalPrice > virtualCard.balance && !hasPartialPaymentOption && !isSkipCheckoutFlow) ||
    disabled;

  const trialInfo = !!price && !!trialDays && (
    <Typography className="p5 m-t-15">
      {t('subscription.payment.trial_info', {
        trial_days: trialDays,
        price,
      })}
    </Typography>
  );

  return (
    <SRFundsPaymentFormStyled>
      {!isSkipCheckoutFlow ? (
        <div className="balance-wrap">
          <div className="balance">
            <Typography className="p5 balance__title" color="textSecondary">
              {t('streamingRewards.balance.available')}
            </Typography>
            <div className="balance-price">
              <Typography className="balance-price__amount">
                {formatBalance(virtualCard.balance, '$')}
              </Typography>
              {topUpAllowed && (
                <Button
                  color="primary"
                  onClick={dialog.onOpen}
                  variant="text"
                  className="balance-price__funds-btn"
                >
                  + {t('buttons.addFunds')}
                </Button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <Typography className="p5">{t('subscription.srWithoutBalance.payment.text')}</Typography>
      )}
      {!isSkipCheckoutFlow && PartialPaymentAllowed && (
        <PartialPayment initialValues={stripeInitValues} loading={loading} disabled={disabled} />
      )}
      {trialInfo}
      <Button
        className="purchase-btn"
        disabled={disabledSubmitBtn}
        color="primary"
        onClick={() => onSubmit({ payment_method_id: virtualCard.payment_method_id })}
        startIcon={<i className="icon-mb-icon-2" />}
      >
        {t('common.purchase')}
      </Button>
      {topUpAllowed && (
        <FundsDialog open={dialog.open} onClose={dialog.onClose} cardReqParams={cardReqParams} />
      )}
      <Loader active={loading || fundsPaymentLoading || loadingUpdateSettings} />
    </SRFundsPaymentFormStyled>
  );
}

SRFundsPaymentForm.defaultProps = {
  loading: false,
  partner: {},
  isSkipCheckoutFlow: false,
};

SRFundsPaymentForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  virtualCard: PropTypes.object.isRequired,
  cardReqParams: PropTypes.object.isRequired,
  totalPrice: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  partner: PropTypes.object,
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  trialDays: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isSkipCheckoutFlow: PropTypes.bool,
  stripeInitValues: PropTypes.object,
};

export default SRFundsPaymentForm;
